import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Kanban } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader, Spinner } from "@/components/UI";
import { formatCurrency, formatDate } from "@/lib/format";

const COLS = ["Draft", "Bidding", "Sent", "Negotiating", "Won", "Lost"];

export default function Pipeline() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const nav = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.listBids()
      .then(setBids)
      .catch(() => toast("Failed to load pipeline", "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const move = async (bid, status) => {
    if (bid.status === status) return;
    try {
      await api.updateBid(bid.id, { status });
      toast(`Moved to ${status}`, "success");
      load();
    } catch {
      toast("Move failed", "error");
    }
  };

  const grouped = COLS.reduce((a, c) => ({ ...a, [c]: bids.filter((b) => b.status === c) }), {});

  if (loading) return <div className="muted text-sm flex items-center gap-2"><Spinner /> Loading…</div>;

  return (
    <div data-testid="page-pipeline">
      <PageHeader
        icon={Kanban}
        title="Pipeline"
        subtitle="Drag through stages using the dropdown on each card"
      />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {COLS.map((col) => (
          <div key={col} className="kanban-col" data-testid={`col-${col}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">{col}</span>
              <span className="text-[10px] font-bold text-slate-500 bg-white rounded-full px-2 py-0.5 border border-slate-200">
                {grouped[col].length}
              </span>
            </div>
            <div className="space-y-2">
              {grouped[col].map((b) => (
                <div
                  key={b.id}
                  data-testid={`pcard-${b.id}`}
                  className="kanban-card"
                  onClick={() => nav(`/bids?id=${b.id}`)}
                >
                  <div className="text-[13px] font-semibold line-clamp-2">{b.title}</div>
                  <div className="text-[11px] muted mt-1 truncate">{b.client_name || "—"}</div>
                  <div className="flex items-center justify-between mt-2 text-[11px]">
                    <span className="muted">{formatDate(b.deadline)}</span>
                    <span className="font-semibold">{formatCurrency(b.bid_amount, b.currency)}</span>
                  </div>
                  <select
                    data-testid={`pmove-${b.id}`}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => move(b, e.target.value)}
                    value={b.status}
                    className="select mt-2 text-[11px] py-1"
                  >
                    {[...COLS, "Cancelled"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              {grouped[col].length === 0 && (
                <div className="text-[11px] muted text-center py-3">No bids</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
